import React, { useEffect, useState } from "react";
import { CircleNotch, PencilSimple, Check, Lock } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Reusable admin-only "Edit Stop-Loss / Target" panel.
 *
 * Used by:
 *   - StockDetailModal (long/short-term breakouts) — endpoint:
 *     PATCH /admin/signals/trade-plan
 *     identifier: { symbol, signal_type, first_detected_at }
 *
 *   - RetestCard (re-test setups, same `db.signals` row) — same endpoint
 *
 *   - CupCard (db.cup_signals collection) — endpoint:
 *     PATCH /admin/cup-signals/trade-plan
 *     identifier: { symbol, kind, breakout_date }
 *
 * Pass `endpoint` + `identifier` to wire it up.
 */
export default function AdminEditTradePlan({
  signal,
  endpoint = "/admin/signals/trade-plan",
  identifier,
  testIdSuffix = "",
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedOk, setSavedOk] = useState(false);
  const [target, setTarget] = useState(() => signal.target ?? "");
  const [stopLoss, setStopLoss] = useState(() => signal.stop_loss ?? "");

  useEffect(() => {
    if (open) {
      setTarget(signal.target ?? "");
      setStopLoss(signal.stop_loss ?? "");
      setErr("");
      setSavedOk(false);
    }
  }, [open, signal.target, signal.stop_loss]);

  const entry = Number(signal.entry || 0);
  const sl = Number(stopLoss);
  const tgt = Number(target);
  const risk = entry - sl;
  const reward = tgt - entry;
  const rr = risk > 0 ? reward / risk : 0;
  const valid = entry > 0 && sl > 0 && sl < entry && tgt > entry;

  const submit = async (e) => {
    e?.preventDefault?.();
    setSavedOk(false);
    if (!valid) {
      setErr(
        entry <= 0
          ? "Cannot edit — entry price missing"
          : sl <= 0 || sl >= entry
            ? `Stop-loss must be > 0 and below entry ₹${entry.toFixed(2)}`
            : `Target must be above entry ₹${entry.toFixed(2)}`,
      );
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.patch(endpoint, {
        ...identifier,
        target: Number(target),
        stop_loss: Number(stopLoss),
      });
      const updated = data?.signal || data?.cup_signal || data;
      if (updated) onSaved?.(updated);
      setSavedOk(true);
      setTimeout(() => {
        setOpen(false);
        setSavedOk(false);
      }, 1400);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Failed to save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const tid = (s) => `admin-edit-${s}${testIdSuffix ? "-" + testIdSuffix : ""}`;

  return (
    <div className="border border-subtle rounded-sm" data-testid={tid("trade-plan")}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid={tid("toggle")}
        className="w-full flex items-center justify-between px-3 py-2 bg-surface hover:bg-surface-hover text-left transition"
      >
        <div className="flex items-center gap-2">
          <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-mod text-mod bg-mod-soft rounded-sm flex items-center gap-1">
            <Lock size={9} weight="fill" /> Admin
          </span>
          <span className="text-xs font-medium text-primary-fg flex items-center gap-1.5">
            <PencilSimple size={12} weight="bold" /> Edit SL / Target
          </span>
          {signal.manually_edited && (
            <span className="text-[10px] font-mono text-secondary-fg ml-1">
              · edited{signal.manually_edited_by ? ` by ${signal.manually_edited_by}` : ""}
            </span>
          )}
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          {open ? "Close" : "Edit"}
        </span>
      </button>

      {open && (
        <form onSubmit={submit} className="px-3 py-3 border-t border-subtle bg-app" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-fg block mb-1.5">
                Stop Loss (₹)
              </label>
              <input
                type="number"
                step="0.01"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                disabled={busy}
                data-testid={tid("sl-input")}
                className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 font-mono text-sm text-neg tabular-nums focus:outline-none focus:border-neg/60"
              />
              <div className="text-[10px] font-mono text-muted-fg mt-1">
                Below entry ₹{entry.toFixed(2)}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-fg block mb-1.5">
                Target (₹)
              </label>
              <input
                type="number"
                step="0.01"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                disabled={busy}
                data-testid={tid("target-input")}
                className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 font-mono text-sm text-pos tabular-nums focus:outline-none focus:border-pos/60"
              />
              <div className="text-[10px] font-mono text-muted-fg mt-1">
                Above entry ₹{entry.toFixed(2)}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-fg block mb-1.5">
                Risk : Reward
              </label>
              <div
                className={
                  "w-full border border-subtle rounded-sm px-3 py-2 font-mono text-sm tabular-nums " +
                  (valid && rr >= 1.5 ? "text-pos" : valid ? "text-mod" : "text-neg")
                }
                data-testid={tid("rr-preview")}
              >
                {valid ? `1 : ${rr.toFixed(2)}` : "—"}
              </div>
              <div className="text-[10px] font-mono text-muted-fg mt-1">
                Auto-calculated
              </div>
            </div>
          </div>

          {err && (
            <div
              className="mb-3 px-3 py-2 border border-neg bg-neg-soft text-neg text-xs font-mono rounded-sm"
              data-testid={tid("err")}
            >
              {err}
            </div>
          )}

          {savedOk && (
            <div
              className="mb-3 px-3 py-2 border text-xs font-mono rounded-sm flex items-center gap-2"
              style={{
                background: "hsl(var(--signal-positive) / 0.12)",
                borderColor: "hsl(var(--signal-positive))",
                color: "hsl(var(--signal-positive))",
              }}
              data-testid={tid("ok")}
            >
              <Check size={12} weight="bold" /> Updated · SL ₹{Number(stopLoss).toFixed(2)} · Target ₹{Number(target).toFixed(2)} · R:R 1:{rr.toFixed(2)}
            </div>
          )}

          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              disabled={busy}
              data-testid={tid("cancel")}
              className="px-4 py-2 text-[11px] font-mono uppercase tracking-widest border border-subtle rounded-sm text-secondary-fg hover:text-primary-fg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid={tid("save")}
              title={!valid ? "Fix SL and Target before updating" : "Save the new SL & Target"}
              className="px-6 py-2.5 text-[11px] font-bold uppercase tracking-widest rounded-sm flex items-center gap-2 transition hover:opacity-90"
              style={{
                background: "hsl(var(--signal-positive))",
                borderWidth: "2px",
                borderStyle: "solid",
                borderColor: "hsl(var(--signal-positive))",
                color: "hsl(var(--bg))",
                opacity: busy || !valid ? 0.55 : 1,
                cursor: busy || !valid ? "not-allowed" : "pointer",
              }}
            >
              {busy ? (
                <>
                  <CircleNotch size={12} weight="bold" className="animate-spin" /> Saving…
                </>
              ) : savedOk ? (
                <>
                  <Check size={12} weight="bold" /> Saved
                </>
              ) : (
                <>
                  <Check size={12} weight="bold" /> Update Trade Plan
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
